import * as React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Container,
  CssBaseline,
  Toolbar,
  Typography,
  CircularProgress,
  Tabs,
  Tab,
  Alert,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Pagination,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  ListItemText,
} from "@mui/material";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import RefreshIcon from "@mui/icons-material/Refresh";
import CodeIcon from "@mui/icons-material/Code";
import BlockIcon from "@mui/icons-material/Block";
import CheckIcon from "@mui/icons-material/Check";
import Navigation from "../components/Navigation";
import ContactDialog from "../components/admin/ContactDialog";
import JsonDataModal from "../components/admin/JsonDataModal";
import SubscriptionDialog from "../components/admin/SubscriptionDialog";
import StateSelect from "../components/admin/StateSelect";
import AddUserToOrgDialog from "../components/admin/AddUserToOrgDialog";
import CreateUserDialog from "../components/admin/CreateUserDialog";
import EditUserDialog from "../components/admin/EditUserDialog";
import DeleteContactDialog from "../components/admin/DeleteContactDialog";
import SelectContactForRegisteredAgentDialog from "../components/admin/SelectContactForRegisteredAgentDialog";
import OrderDialog from "../components/admin/OrderDialog";
import DeleteOrderDialog from "../components/admin/DeleteOrderDialog";
import DeleteSubscriptionDialog from "../components/admin/DeleteSubscriptionDialog";
import { useApi } from "../hooks/useApi";
import { formatOrganizationUserRoleLabel } from "../constants/organizationUserRoles.js";

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function AdminOrganizationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const api = useApi();

  // Initialize tab from URL parameter, default to 0
  const getInitialTab = () => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 5) {
        return tabIndex;
      }
    }
    return 0;
  };
  
  const [tabValue, setTabValue] = React.useState(getInitialTab);
  const [organization, setOrganization] = React.useState(null);
  const [users, setUsers] = React.useState([]);
  const [contacts, setContacts] = React.useState([]);
  const [orders, setOrders] = React.useState([]);
  const [subscriptions, setSubscriptions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [loadingOrders, setLoadingOrders] = React.useState(false);
  const [loadingContacts, setLoadingContacts] = React.useState(false);
  const [loadingSubscriptions, setLoadingSubscriptions] = React.useState(false);
  const [loadingUsers, setLoadingUsers] = React.useState(false);
  const [refreshingContacts, setRefreshingContacts] = React.useState(false);
  const [refreshingSubscriptions, setRefreshingSubscriptions] = React.useState(false);
  const [refreshingOrders, setRefreshingOrders] = React.useState(false);
  const [refreshingUsers, setRefreshingUsers] = React.useState(false);
  const [editSubscriptionDialogOpen, setEditSubscriptionDialogOpen] = React.useState(false);
  const [addSubscriptionDialogOpen, setAddSubscriptionDialogOpen] = React.useState(false);
  const [deleteSubscriptionDialogOpen, setDeleteSubscriptionDialogOpen] = React.useState(false);
  const [subscriptionToDelete, setSubscriptionToDelete] = React.useState(null);
  const [editingSubscription, setEditingSubscription] = React.useState(null);
  const [subscriptionFormData, setSubscriptionFormData] = React.useState({});
  const [editUserDialogOpen, setEditUserDialogOpen] = React.useState(false);
  const [editingUser, setEditingUser] = React.useState(null);
  const [userFormData, setUserFormData] = React.useState({});
  const [editOrderDialogOpen, setEditOrderDialogOpen] = React.useState(false);
  const [addOrderDialogOpen, setAddOrderDialogOpen] = React.useState(false);
  const [deleteOrderDialogOpen, setDeleteOrderDialogOpen] = React.useState(false);
  const [orderToDelete, setOrderToDelete] = React.useState(null);
  const [editingOrder, setEditingOrder] = React.useState(null);
  const [orderFormData, setOrderFormData] = React.useState({});
  const [error, setError] = React.useState(null);
  const [editing, setEditing] = React.useState(false);
  const [formData, setFormData] = React.useState({});
  const [addUserDialogOpen, setAddUserDialogOpen] = React.useState(false);
  const [createUserDialogOpen, setCreateUserDialogOpen] = React.useState(false);
  const [selectedUserId, setSelectedUserId] = React.useState("");
  const [selectedUserRole, setSelectedUserRole] = React.useState("viewer");
  const [availableUsers, setAvailableUsers] = React.useState([]);
  const [loadingAvailableUsers, setLoadingAvailableUsers] = React.useState(false);
  const [userSearchQuery, setUserSearchQuery] = React.useState("");
  const [userPagination, setUserPagination] = React.useState({
    total: 0,
    limit: 10,
    offset: 0,
    hasMore: false,
  });
  const [newUserFormData, setNewUserFormData] = React.useState({
    name: "",
    email: "",
  });
  const [editContactDialogOpen, setEditContactDialogOpen] = React.useState(false);
  const [addContactDialogOpen, setAddContactDialogOpen] = React.useState(false);
  const [deleteContactDialogOpen, setDeleteContactDialogOpen] = React.useState(false);
  const [contactToDelete, setContactToDelete] = React.useState(null);
  const [editingContact, setEditingContact] = React.useState(null);
  const [contactFormData, setContactFormData] = React.useState({});
  const [contactJsonModalOpen, setContactJsonModalOpen] = React.useState(false);
  const [selectedContactForJson, setSelectedContactForJson] = React.useState(null);
  const [subscriptionJsonModalOpen, setSubscriptionJsonModalOpen] = React.useState(false);
  const [selectedSubscriptionForJson, setSelectedSubscriptionForJson] = React.useState(null);
  const [registeredAgentJsonModalOpen, setRegisteredAgentJsonModalOpen] = React.useState(false);
  const [selectedRegisteredAgentForJson, setSelectedRegisteredAgentForJson] = React.useState(null);
  const [editingRegisteredAgent, setEditingRegisteredAgent] = React.useState(false);
  const [registeredAgentFormData, setRegisteredAgentFormData] = React.useState({});
  const [selectContactDialogOpen, setSelectContactDialogOpen] = React.useState(false);
  const [selectedContactForRegisteredAgent, setSelectedContactForRegisteredAgent] = React.useState(null);
  const [registeredAgentRenewalDate, setRegisteredAgentRenewalDate] = React.useState("");
  const [registeredAgentRenewalDateMonth, setRegisteredAgentRenewalDateMonth] = React.useState("");
  const [registeredAgentRenewalDateDay, setRegisteredAgentRenewalDateDay] = React.useState("");
  const [organizationJsonModalOpen, setOrganizationJsonModalOpen] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });
  const [orgNameError, setOrgNameError] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [websiteError, setWebsiteError] = React.useState('');
  const [permissionsError, setPermissionsError] = React.useState('');

  // Fetch organization details
  const fetchOrganization = React.useCallback(async () => {
    try {
      const data = await api.get(`/admin/organizations/${id}`);
      setOrganization(data);
      // Normalize zip code and EIN to digits-only for consistent formatting
      const normalizedData = {
        ...data,
        physical_zip: data.physical_zip ? data.physical_zip.replace(/\D/g, '').slice(0, 9) : data.physical_zip,
        organization_ein: data.organization_ein ? data.organization_ein.replace(/\D/g, '').slice(0, 9) : data.organization_ein
      };
      setFormData(normalizedData);
    } catch (err) {
      console.error("Error fetching organization:", err);
      setError(err.message || "Failed to fetch organization");
    }
  }, [id, api]);

  // Fetch organization users
  const fetchUsers = React.useCallback(async (showLoading = false) => {
    const startTime = Date.now();
    
    if (showLoading) {
      setRefreshingUsers(true);
      setLoadingUsers(true); // Show skeleton table when refreshing
    } else {
      setLoadingUsers(true);
    }
    
    try {
      const data = await api.get(`/admin/organizations/${id}/users`);
      setUsers(Array.isArray(data) ? data : []);
      
      // Ensure minimum 1 second delay when refreshing to show skeleton UI
      if (showLoading) {
        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, 1000 - elapsed);
        await new Promise(resolve => setTimeout(resolve, remainingDelay));
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      // Still ensure minimum delay even on error
      if (showLoading) {
        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, 1000 - elapsed);
        await new Promise(resolve => setTimeout(resolve, remainingDelay));
      }
    } finally {
      if (showLoading) {
        setRefreshingUsers(false);
        setLoadingUsers(false); // Hide skeleton table when done refreshing
      } else {
        setLoadingUsers(false);
      }
    }
  }, [id, api]);

  // Fetch organization contacts
  const fetchContacts = React.useCallback(async (showLoading = false) => {
    if (showLoading) {
      setRefreshingContacts(true);
      setLoadingContacts(true); // Show skeleton table when refreshing
    } else {
      setLoadingContacts(true);
    }
    
    // Add 1 second delay when refreshing to show skeleton UI
    const startTime = Date.now();
    
    try {
      const data = await api.get(`/admin/organizations/${id}/contacts`);
      
      // Ensure minimum 1 second delay when refreshing
      if (showLoading) {
        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, 1000 - elapsed);
        await new Promise(resolve => setTimeout(resolve, remainingDelay));
      }
      
      setContacts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching contacts:", err);
      // Still ensure minimum delay even on error
      if (showLoading) {
        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, 1000 - elapsed);
        await new Promise(resolve => setTimeout(resolve, remainingDelay));
      }
    } finally {
      if (showLoading) {
        setRefreshingContacts(false);
        setLoadingContacts(false); // Hide skeleton table when done refreshing
      } else {
        setLoadingContacts(false);
      }
    }
  }, [id, api]);

  // Fetch organization orders
  const fetchOrders = React.useCallback(async (showLoading = false) => {
    if (showLoading) {
      setRefreshingOrders(true);
      setLoadingOrders(true); // Show skeleton table when refreshing
    } else {
      setLoadingOrders(true);
    }
    
    // Add 1 second delay when refreshing to show skeleton UI
    const startTime = Date.now();
    
    try {
      const data = await api.get(`/admin/organizations/${id}/orders`);
      
      // Ensure minimum 1 second delay when refreshing
      if (showLoading) {
        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, 1000 - elapsed);
        await new Promise(resolve => setTimeout(resolve, remainingDelay));
      }
      
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching orders:", err);
      // Still ensure minimum delay even on error
      if (showLoading) {
        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, 1000 - elapsed);
        await new Promise(resolve => setTimeout(resolve, remainingDelay));
      }
    } finally {
      if (showLoading) {
        setRefreshingOrders(false);
        setLoadingOrders(false); // Hide skeleton table when done refreshing
      } else {
        setLoadingOrders(false);
      }
    }
  }, [id, api]);

  // Fetch organization subscriptions
  const fetchSubscriptions = React.useCallback(async (showLoading = false) => {
    if (showLoading) {
      setRefreshingSubscriptions(true);
      setLoadingSubscriptions(true); // Show skeleton table when refreshing
    } else {
      setLoadingSubscriptions(true);
    }
    
    // Add 1 second delay when refreshing to show skeleton UI
    const startTime = Date.now();
    
    try {
      const data = await api.get(`/admin/organizations/${id}/subscriptions`);
      
      // Ensure minimum 1 second delay when refreshing
      if (showLoading) {
        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, 1000 - elapsed);
        await new Promise(resolve => setTimeout(resolve, remainingDelay));
      }
      
      setSubscriptions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching subscriptions:", err);
      // Still ensure minimum delay even on error
      if (showLoading) {
        const elapsed = Date.now() - startTime;
        const remainingDelay = Math.max(0, 1000 - elapsed);
        await new Promise(resolve => setTimeout(resolve, remainingDelay));
      }
    } finally {
      if (showLoading) {
        setRefreshingSubscriptions(false);
        setLoadingSubscriptions(false); // Hide skeleton table when done refreshing
      } else {
        setLoadingSubscriptions(false);
      }
    }
  }, [id, api]);


  React.useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchOrganization(),
        fetchUsers(),
        fetchContacts(),
        fetchOrders(),
        fetchSubscriptions(),
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchOrganization, fetchUsers, fetchContacts, fetchOrders, fetchSubscriptions]);

  // Validate required fields
  const validateRequired = (value, fieldName) => {
    if (!value || value.trim() === '') {
      return `${fieldName} is required`;
    }
    return '';
  };

  // Validate email format
  const validateEmail = (email) => {
    if (!email || email.trim() === '') {
      return ''; // Empty email is allowed (optional field)
    }
    // RFC 5322 compliant email regex (simplified version)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address (e.g., name@example.com)';
    }
    return '';
  };

  // Validate URL format
  // Validate domain format (without requiring protocol)
  const validateURL = (url) => {
    if (!url || url.trim() === '') {
      return ''; // Empty URL is allowed (optional field)
    }
    
    const trimmedUrl = url.trim();
    
    // Remove protocol if present (for validation purposes)
    const domainOnly = trimmedUrl.replace(/^https?:\/\//i, '').replace(/\/$/, '');
    
    // Domain regex pattern: allows subdomains, domain, and TLD
    // Examples: example.com, subdomain.example.com, example.co.uk
    const domainPattern = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/i;
    
    if (!domainPattern.test(domainOnly)) {
      return 'Please enter a valid domain (e.g., example.com)';
    }
    
    return '';
  };

  // Format phone number as (XXX) XXX-XXXX
  const formatPhoneNumber = (value) => {
    if (!value) return '';
    // Remove all non-digit characters
    const phoneNumber = value.replace(/\D/g, '');
    
    // Format based on length
    if (phoneNumber.length === 0) return '';
    if (phoneNumber.length <= 3) return `(${phoneNumber}`;
    if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  // Get formatted display value for phone field
  const getFormattedPhone = (value) => {
    if (!value) return '';
    return formatPhoneNumber(value);
  };

  // Handle phone number input change
  const handlePhoneChange = (value) => {
    // Store digits only, format for display
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, phone: digitsOnly });
  };

  // Handle office phone number input change
  const handleOfficePhoneChange = (value) => {
    // Store digits only, format for display
    const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, office_phone: digitsOnly });
  };

  // Format zip code as 12345 or 12345-6789 (for display)
  const formatZipCode = (value) => {
    if (!value) return '';
    // Remove all non-digit characters
    const zipCode = value.replace(/\D/g, '');
    
    // Limit to 9 digits maximum
    const limitedZip = zipCode.slice(0, 9);
    
    // Format based on length
    if (limitedZip.length === 0) return '';
    if (limitedZip.length <= 5) return limitedZip;
    // Format as 12345-6789 for 9 digits
    return `${limitedZip.slice(0, 5)}-${limitedZip.slice(5, 9)}`;
  };

  // Get formatted display value for zip field
  const getFormattedZip = (value) => {
    if (!value) return '';
    return formatZipCode(value);
  };

  // Handle zip code input change - store digits only, display formatted
  const handleZipChange = (value) => {
    // Extract digits only and limit to 9 digits
    const digitsOnly = value.replace(/\D/g, '').slice(0, 9);
    setFormData({ ...formData, physical_zip: digitsOnly });
  };

  // Format EIN as XX-XXXXXXX
  const formatEIN = (value) => {
    if (!value) return '';
    // Remove all non-digit characters
    const einDigits = value.replace(/\D/g, '');
    
    // Limit to 9 digits maximum
    const limitedEIN = einDigits.slice(0, 9);
    
    // Format based on length
    if (limitedEIN.length === 0) return '';
    if (limitedEIN.length <= 2) return limitedEIN;
    // Format as XX-XXXXXXX for 9 digits
    return `${limitedEIN.slice(0, 2)}-${limitedEIN.slice(2, 9)}`;
  };

  // Get formatted display value for EIN field
  const getFormattedEIN = (value) => {
    if (!value) return '';
    return formatEIN(value);
  };

  // Handle EIN input change - store digits only, display formatted
  const handleEINChange = (value) => {
    // Extract digits only and limit to 9 digits
    const digitsOnly = value.replace(/\D/g, '').slice(0, 9);
    setFormData({ ...formData, organization_ein: digitsOnly });
  };

  // Validate fields when editing mode is enabled or formData changes
  React.useEffect(() => {
    if (editing) {
      const orgNameErr = validateRequired(formData.organization_name || '', 'Organization name');
      const emailErr = validateEmail(formData.email || '');
      const websiteErr = validateURL(formData.website || '');
      setOrgNameError(orgNameErr);
      setEmailError(emailErr);
      setWebsiteError(websiteErr);
    } else {
      setOrgNameError('');
      setEmailError('');
      setWebsiteError('');
    }
  }, [editing, formData.organization_name, formData.email, formData.website]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Update URL parameter to persist tab selection
    setSearchParams({ tab: newValue.toString() });
  };
  
  // Sync tab value when URL parameter changes (e.g., browser back/forward)
  React.useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 5) {
        setTabValue(tabIndex);
      }
    } else {
      // If no tab param, default to 0
      setTabValue(0);
    }
  }, [searchParams]);

  const handleSave = async () => {
    // Validate required fields
    const orgNameErr = validateRequired(formData.organization_name || '', 'Organization name');
    const emailErr = validateEmail(formData.email || '');
    const websiteErr = validateURL(formData.website || '');
    
    setOrgNameError(orgNameErr);
    setEmailError(emailErr);
    setWebsiteError(websiteErr);
    
    if (orgNameErr || emailErr || websiteErr) {
      setError('Please fix the validation errors before saving');
      return;
    }

    try {
      // Remove account_name from formData as it's not part of the database schema
      const { account_name, ...formDataToSend } = formData;

      const updated = await api.patch(`/admin/organizations/${id}`, formDataToSend);
      setOrganization(updated);
      // Normalize zip code and EIN to digits-only for consistent formatting
      const normalizedUpdated = {
        ...updated,
        physical_zip: updated.physical_zip ? updated.physical_zip.replace(/\D/g, '').slice(0, 9) : updated.physical_zip,
        organization_ein: updated.organization_ein ? updated.organization_ein.replace(/\D/g, '').slice(0, 9) : updated.organization_ein
      };
      setFormData(normalizedUpdated);
      setEditing(false);
      setOrgNameError('');
      setEmailError('');
      setWebsiteError('');
      setError(null);
    } catch (err) {
      console.error("Error updating organization:", err);
      setError(err.message || "Failed to update organization");
    }
  };

  const handleAddUser = async (userId = null, role = "viewer") => {
    const userToAdd = userId || selectedUserId;
    const roleToUse = role || selectedUserRole;
    
    if (!userToAdd) return;

    try {
      await api.post(`/admin/organizations/${id}/users`, {
        user_id: userToAdd,
        role: roleToUse,
      });

      setSnackbar({
        open: true,
        message: "User added successfully",
        severity: "success",
      });

      // Refresh users list
      fetchUsers();
      // Refresh available users to update the "Already Added" status
      fetchAvailableUsers(userSearchQuery);
    } catch (err) {
      console.error("Error adding user:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to add user",
        severity: "error",
      });
    }
  };

  const handleCreateUser = async () => {
    if (!newUserFormData.email || !newUserFormData.email.trim()) {
      setSnackbar({
        open: true,
        message: "Email is required",
        severity: "error",
      });
      return;
    }

    try {
      await api.post("/admin/users", {
        email: newUserFormData.email.trim(),
        name: newUserFormData.name.trim() || null,
      });
      
      setSnackbar({
        open: true,
        message: "User created successfully",
        severity: "success",
      });

      // Close dialog and reset form
      setCreateUserDialogOpen(false);
      setNewUserFormData({ name: "", email: "" });
      
      // Refresh available users list
      fetchAvailableUsers(userSearchQuery);
    } catch (err) {
      console.error("Error creating user:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to create user",
        severity: "error",
      });
    }
  };

  const handleRemoveUser = async (userId) => {
    if (!window.confirm("Are you sure you want to remove this user from the organization?")) {
      return;
    }

    try {
      await api.delete(`/admin/organizations/${id}/users/${userId}`);
      setSnackbar({ open: true, message: "User removed successfully", severity: "success" });
      fetchUsers();
    } catch (err) {
      console.error("Error removing user:", err);
      setSnackbar({ open: true, message: err.message || "Failed to remove user", severity: "error" });
    }
  };


  // Fetch available users for adding to organization (with search and pagination)
  const fetchAvailableUsers = React.useCallback(async (searchQuery = "", offset = 0) => {
    setLoadingAvailableUsers(true);
    try {
      const params = new URLSearchParams();
      if (searchQuery && searchQuery.trim()) {
        params.append("search", searchQuery.trim());
      }
      params.append("limit", "10");
      params.append("offset", offset.toString());

      const data = await api.get(`/admin/users?${params.toString()}`);
      
      // Handle both old format (array) and new format (paginated)
      if (Array.isArray(data)) {
        setAvailableUsers(data);
        setUserPagination({
          total: data.length,
          limit: 10,
          offset: 0,
          hasMore: false,
        });
      } else {
        setAvailableUsers(data.data || []);
        setUserPagination(data.pagination || {
          total: 0,
          limit: 10,
          offset: 0,
          hasMore: false,
        });
      }
    } catch (err) {
      console.error("Error fetching available users:", err);
      setAvailableUsers([]); // Set empty array on error
      setUserPagination({
        total: 0,
        limit: 10,
        offset: 0,
        hasMore: false,
      });
    } finally {
      setLoadingAvailableUsers(false);
    }
  }, [api]);

  // Debounced search handler
  const searchTimeoutRef = React.useRef(null);
  const handleSearchChange = (query) => {
    setUserSearchQuery(query);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Debounce API call by 300ms and reset to first page
    searchTimeoutRef.current = setTimeout(() => {
      fetchAvailableUsers(query, 0);
    }, 300);
  };

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Open add user dialog and fetch available users
  const handleOpenAddUserDialog = () => {
    setAddUserDialogOpen(true);
    setUserSearchQuery("");
    setSelectedUserId("");
    setSelectedUserRole("viewer");
    fetchAvailableUsers(""); // Fetch all users initially
  };

  const handleUserPageChange = (newOffset) => {
    fetchAvailableUsers(userSearchQuery, newOffset);
  };

  const handleEditUserClick = (userOrg) => {
    setEditingUser(userOrg);
    setUserFormData({
      role: userOrg.role || "viewer",
      is_active: userOrg.is_active !== undefined ? userOrg.is_active : true,
      permissions: userOrg.permissions || {},
    });
    setEditUserDialogOpen(true);
  };

  const handleCloseUserDialog = () => {
    setEditUserDialogOpen(false);
    setEditingUser(null);
    setUserFormData({});
    setPermissionsError('');
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;

    // Validate permissions JSON before saving
    if (permissionsError) {
      setSnackbar({
        open: true,
        message: "Please fix the permissions JSON error before saving",
        severity: "error",
      });
      return;
    }

    try {
      
      // Prepare the update payload
      const updatePayload = {
        role: userFormData.role || "viewer",
        is_active: userFormData.is_active !== undefined ? userFormData.is_active : true,
        permissions: userFormData.permissions || {},
      };

      await api.patch(`/admin/organizations/${id}/users/${editingUser.user_id}`, updatePayload);

      setSnackbar({
        open: true,
        message: "User updated successfully",
        severity: "success",
      });

      handleCloseUserDialog();
      fetchUsers();
    } catch (err) {
      console.error("Error updating user:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to update user",
        severity: "error",
      });
    }
  };

  const handleEditContact = (contact) => {
    setEditingContact(contact);
    const raw = contact.meta_data;
    const metaStr =
      raw == null
        ? "{}"
        : typeof raw === "string"
          ? raw
          : typeof raw === "object" && !Array.isArray(raw)
            ? JSON.stringify(raw, null, 2)
            : "{}";
    setContactFormData({
      first_name: contact.first_name || "",
      last_name: contact.last_name || "",
      role: contact.role || "",
      email: contact.email || "",
      phone: contact.phone || "",
      mobile: contact.mobile || "",
      street: contact.street || "",
      street_2: contact.street_2 || "",
      city: contact.city || "",
      state: contact.state || "",
      zip: contact.zip || "",
      country: contact.country || "",
      is_board_member: contact.is_board_member || false,
      meta_data: metaStr,
    });
    setEditContactDialogOpen(true);
  };

  const handleAddContact = () => {
    setEditingContact(null);
    setContactFormData({
      first_name: "",
      last_name: "",
      role: "",
      email: "",
      phone: "",
      mobile: "",
      street: "",
      street_2: "",
      city: "",
      state: "",
      zip: "",
      country: "",
      is_board_member: false,
      meta_data: "{}",
    });
    setAddContactDialogOpen(true);
  };

  const handleCloseContactDialog = () => {
    setEditContactDialogOpen(false);
    setAddContactDialogOpen(false);
    setEditingContact(null);
    setContactFormData({});
  };

  const handleSaveContact = async () => {
    try {
      
      if (editingContact) {
        // Update existing contact
        await api.patch(`/admin/contacts/${editingContact.id}`, contactFormData);

        setSnackbar({
          open: true,
          message: "Contact updated successfully",
          severity: "success",
        });
      } else {
        // Create new contact
        await api.post("/admin/contacts", {
          ...contactFormData,
          organization_id: id,
        });

        setSnackbar({
          open: true,
          message: "Contact added successfully",
          severity: "success",
        });
      }

      handleCloseContactDialog();
      fetchContacts();
    } catch (err) {
      console.error("Error saving contact:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to save contact",
        severity: "error",
      });
    }
  };

  const handleDeleteContactClick = (contact) => {
    setContactToDelete(contact);
    setDeleteContactDialogOpen(true);
  };

  const handleDeleteContactConfirm = async () => {
    if (!contactToDelete) return;

    try {
      await api.delete(`/admin/contacts/${contactToDelete.id}`);

      setSnackbar({
        open: true,
        message: "Contact deleted successfully",
        severity: "success",
      });

      setDeleteContactDialogOpen(false);
      setContactToDelete(null);
      fetchContacts();
    } catch (err) {
      console.error("Error deleting contact:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to delete contact",
        severity: "error",
      });
    }
  };

  const handleDeleteContactCancel = () => {
    setDeleteContactDialogOpen(false);
    setContactToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Subscription handlers
  const handleEditSubscriptionClick = (subscription) => {
    setEditingSubscription(subscription);
    setSubscriptionFormData({
      status: subscription.status || "pending",
      plan: subscription.plan || "",
      plan_legacy: subscription.plan_legacy || "",
      zoho_organization_id: subscription.zoho_organization_id || "",
      start_date: subscription.start_date ? new Date(subscription.start_date).toISOString().split("T")[0] : "",
      end_date: subscription.end_date ? new Date(subscription.end_date).toISOString().split("T")[0] : "",
      renewal_date: subscription.renewal_date ? new Date(subscription.renewal_date).toISOString().split("T")[0] : "",
      next_billing_date: subscription.next_billing_date ? new Date(subscription.next_billing_date).toISOString().split("T")[0] : "",
      cancelled_date: subscription.cancelled_date ? new Date(subscription.cancelled_date).toISOString().split("T")[0] : "",
      amount: subscription.amount || "",
      is_recurring: subscription.is_recurring || false,
      auto_renew: subscription.auto_renew || false,
      payment_system: subscription.payment_system || "",
      payment_method_id: subscription.payment_method_id || "",
      external_subscription_id: subscription.external_subscription_id || "",
      source: subscription.source || "",
      notes: subscription.notes || "",
      cancellation_reason: subscription.cancellation_reason || "",
    });
    setEditSubscriptionDialogOpen(true);
  };

  const handleAddSubscriptionClick = () => {
    setEditingSubscription(null);
    setSubscriptionFormData({
      status: "pending",
      plan: "",
      plan_legacy: "",
      zoho_organization_id: "",
      start_date: "",
      end_date: "",
      renewal_date: "",
      next_billing_date: "",
      cancelled_date: "",
      amount: "",
      is_recurring: false,
      auto_renew: false,
      payment_system: "",
      payment_method_id: "",
      external_subscription_id: "",
      source: "",
      notes: "",
      cancellation_reason: "",
    });
    setAddSubscriptionDialogOpen(true);
  };

  const handleCloseSubscriptionDialog = () => {
    setEditSubscriptionDialogOpen(false);
    setAddSubscriptionDialogOpen(false);
    setEditingSubscription(null);
    setSubscriptionFormData({});
  };

  const handleSaveSubscription = async () => {
    try {
      
      // Prepare form data
      const formDataToSend = {
        organization_id: id,
        status: subscriptionFormData.status || "pending",
        plan: subscriptionFormData.plan || null,
        plan_legacy: subscriptionFormData.plan_legacy || null,
        zoho_organization_id: subscriptionFormData.zoho_organization_id || null,
        start_date: subscriptionFormData.start_date ? new Date(subscriptionFormData.start_date).toISOString() : null,
        end_date: subscriptionFormData.end_date ? new Date(subscriptionFormData.end_date).toISOString() : null,
        renewal_date: subscriptionFormData.renewal_date ? new Date(subscriptionFormData.renewal_date).toISOString() : null,
        next_billing_date: subscriptionFormData.next_billing_date ? new Date(subscriptionFormData.next_billing_date).toISOString() : null,
        cancelled_date: subscriptionFormData.cancelled_date ? new Date(subscriptionFormData.cancelled_date).toISOString() : null,
        amount: subscriptionFormData.amount ? parseFloat(subscriptionFormData.amount) : null,
        is_recurring: subscriptionFormData.is_recurring || false,
        auto_renew: subscriptionFormData.auto_renew || false,
        payment_system: subscriptionFormData.payment_system || null,
        payment_method_id: subscriptionFormData.payment_method_id || null,
        external_subscription_id: subscriptionFormData.external_subscription_id || null,
        source: subscriptionFormData.source || null,
        notes: subscriptionFormData.notes || null,
        cancellation_reason: subscriptionFormData.cancellation_reason || null,
      };

      if (editingSubscription) {
        // Update existing subscription
        await api.patch(`/admin/subscriptions/${editingSubscription.id}`, formDataToSend);

        setSnackbar({
          open: true,
          message: "Subscription updated successfully",
          severity: "success",
        });
      } else {
        // Create new subscription
        await api.post("/admin/subscriptions", formDataToSend);

        setSnackbar({
          open: true,
          message: "Subscription added successfully",
          severity: "success",
        });
      }

      handleCloseSubscriptionDialog();
      fetchSubscriptions();
    } catch (err) {
      console.error("Error saving subscription:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to save subscription",
        severity: "error",
      });
    }
  };

  const handleDeleteSubscriptionClick = (subscription) => {
    setSubscriptionToDelete(subscription);
    setDeleteSubscriptionDialogOpen(true);
  };

  const handleDeleteSubscriptionConfirm = async () => {
    if (!subscriptionToDelete) return;

    try {
      await api.delete(`/admin/subscriptions/${subscriptionToDelete.id}`);

      setSnackbar({
        open: true,
        message: "Subscription deleted successfully",
        severity: "success",
      });

      setDeleteSubscriptionDialogOpen(false);
      setSubscriptionToDelete(null);
      fetchSubscriptions();
    } catch (err) {
      console.error("Error deleting subscription:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to delete subscription",
        severity: "error",
      });
    }
  };

  const handleDeleteSubscriptionCancel = () => {
    setDeleteSubscriptionDialogOpen(false);
    setSubscriptionToDelete(null);
  };

  // Order handlers
  const handleEditOrderClick = (order) => {
    setEditingOrder(order);
    setOrderFormData({
      order_number: order.order_number || "",
      order_date: order.order_date ? new Date(order.order_date).toISOString().split("T")[0] : "",
      product_name: order.product_name || "",
      status: order.status || "Pending",
      amount: order.amount || "",
      description: order.description || "",
      notes: order.notes || "",
    });
    setEditOrderDialogOpen(true);
  };

  const handleAddOrderClick = () => {
    setEditingOrder(null);
    setOrderFormData({
      order_number: "",
      order_date: "",
      product_name: "",
      status: "Pending",
      amount: "",
      description: "",
      notes: "",
    });
    setAddOrderDialogOpen(true);
  };

  const handleCloseOrderDialog = () => {
    setEditOrderDialogOpen(false);
    setAddOrderDialogOpen(false);
    setEditingOrder(null);
    setOrderFormData({});
  };

  const handleSaveOrder = async () => {
    try {
      
      // Prepare form data
      const formDataToSend = {
        organization_id: id,
        order_number: orderFormData.order_number || null,
        order_date: orderFormData.order_date ? new Date(orderFormData.order_date).toISOString() : null,
        product_name: orderFormData.product_name || null,
        status: orderFormData.status || "Pending",
        amount: orderFormData.amount ? parseFloat(orderFormData.amount) : null,
        description: orderFormData.description || null,
        notes: orderFormData.notes || null,
      };

      if (editingOrder) {
        // Update existing order
        await api.patch(`/admin/orders/${editingOrder.id}`, formDataToSend);

        setSnackbar({
          open: true,
          message: "Order updated successfully",
          severity: "success",
        });
      } else {
        // Create new order
        await api.post("/admin/orders", formDataToSend);

        setSnackbar({
          open: true,
          message: "Order added successfully",
          severity: "success",
        });
      }

      handleCloseOrderDialog();
      fetchOrders();
    } catch (err) {
      console.error("Error saving order:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to save order",
        severity: "error",
      });
    }
  };

  const handleDeleteOrderClick = (order) => {
    setOrderToDelete(order);
    setDeleteOrderDialogOpen(true);
  };

  const handleSelectContactForRegisteredAgent = async (contact) => {
    if (!contact) return;
    
    // If editing, update the form data instead of saving immediately
    if (editingRegisteredAgent) {
      setRegisteredAgentFormData({
        ...registeredAgentFormData,
        registered_agent: contact.id,
        registered_agent_name: "",
        registered_agent_street: "",
        registered_agent_city: "",
        registered_agent_state: "",
        registered_agent_zip: "",
        registered_agent_use_different_address: false,
        registered_agent_renewal_date: registeredAgentRenewalDate || "",
        registered_agent_renewal_date_month: registeredAgentRenewalDateMonth || "",
        registered_agent_renewal_date_day: registeredAgentRenewalDateDay || "",
      });
      setSelectContactDialogOpen(false);
      setSelectedContactForRegisteredAgent(null);
      setRegisteredAgentRenewalDate("");
      setRegisteredAgentRenewalDateMonth("");
      setRegisteredAgentRenewalDateDay("");
      setSnackbar({
        open: true,
        message: "Contact selected. Click Save to apply changes.",
        severity: "success",
      });
      return;
    }
    
    // If not editing, save immediately
    try {
      
      // When a contact is selected, set registered_agent to contact ID and clear other fields
      // Convert empty strings to null for date fields
      const updateData = {
        registered_agent: contact.id,
        registered_agent_name: null,
        registered_agent_type: null,
        registered_agent_street: null,
        registered_agent_city: null,
        registered_agent_state: null,
        registered_agent_zip: null,
        registered_agent_use_different_address: false,
        registered_agent_renewal_date: registeredAgentRenewalDate || null,
        registered_agent_renewal_date_month: registeredAgentRenewalDateMonth || null,
        registered_agent_renewal_date_day: registeredAgentRenewalDateDay || null,
      };
      
      const updated = await api.patch(`/admin/organizations/${id}`, updateData);
      setOrganization(updated);
      setSelectContactDialogOpen(false);
      setSelectedContactForRegisteredAgent(null);
      setRegisteredAgentRenewalDate("");
      setRegisteredAgentRenewalDateMonth("");
      setRegisteredAgentRenewalDateDay("");
      setSnackbar({
        open: true,
        message: "Registered agent set to contact successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Error setting registered agent contact:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to set registered agent contact",
        severity: "error",
      });
    }
  };

  const handleClearRegisteredAgentContact = async () => {
    try {
      
      // Clear the registered_agent link, allowing manual entry
      const updateData = {
        registered_agent: null,
      };
      
      const updated = await api.patch(`/admin/organizations/${id}`, updateData);
      setOrganization(updated);
      setSnackbar({
        open: true,
        message: "Registered agent contact link cleared. You can now enter manual fields.",
        severity: "success",
      });
    } catch (err) {
      console.error("Error clearing registered agent contact:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to clear registered agent contact",
        severity: "error",
      });
    }
  };

  const handleSaveRegisteredAgent = async () => {
    try {
      
      // Prepare data to save
      // If registered_agent is set, include it; otherwise send the other fields
      const registeredAgentDataToSave = { ...registeredAgentFormData };
      
      // If registered_agent is set, clear the other fields
      if (registeredAgentDataToSave.registered_agent) {
        registeredAgentDataToSave.registered_agent_name = null;
        registeredAgentDataToSave.registered_agent_street = null;
        registeredAgentDataToSave.registered_agent_city = null;
        registeredAgentDataToSave.registered_agent_state = null;
        registeredAgentDataToSave.registered_agent_zip = null;
        registeredAgentDataToSave.registered_agent_use_different_address = false;
      }
      
      // Convert empty strings to null for date fields (PostgreSQL requires null, not empty strings)
      if (registeredAgentDataToSave.registered_agent_renewal_date === "") {
        registeredAgentDataToSave.registered_agent_renewal_date = null;
      }
      if (registeredAgentDataToSave.registered_agent_renewal_date_month === "") {
        registeredAgentDataToSave.registered_agent_renewal_date_month = null;
      }
      if (registeredAgentDataToSave.registered_agent_renewal_date_day === "") {
        registeredAgentDataToSave.registered_agent_renewal_date_day = null;
      }
      
      // Convert empty strings to null for other string fields that might be dates or should be null
      if (registeredAgentDataToSave.registered_agent_name === "") {
        registeredAgentDataToSave.registered_agent_name = null;
      }
      if (registeredAgentDataToSave.registered_agent_type === "") {
        registeredAgentDataToSave.registered_agent_type = null;
      }
      if (registeredAgentDataToSave.registered_agent_street === "") {
        registeredAgentDataToSave.registered_agent_street = null;
      }
      if (registeredAgentDataToSave.registered_agent_city === "") {
        registeredAgentDataToSave.registered_agent_city = null;
      }
      if (registeredAgentDataToSave.registered_agent_state === "") {
        registeredAgentDataToSave.registered_agent_state = null;
      }
      if (registeredAgentDataToSave.registered_agent_zip === "") {
        registeredAgentDataToSave.registered_agent_zip = null;
      }
      
      const updated = await api.patch(`/admin/organizations/${id}`, registeredAgentDataToSave);
      setOrganization(updated);
      setEditingRegisteredAgent(false);
      setSnackbar({
        open: true,
        message: "Registered agent updated successfully",
        severity: "success",
      });
    } catch (err) {
      console.error("Error updating registered agent:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to update registered agent",
        severity: "error",
      });
    }
  };

  const handleDeleteOrderConfirm = async () => {
    if (!orderToDelete) return;

    try {
      await api.delete(`/admin/orders/${orderToDelete.id}`);

      setSnackbar({
        open: true,
        message: "Order deleted successfully",
        severity: "success",
      });

      setDeleteOrderDialogOpen(false);
      setOrderToDelete(null);
      fetchOrders();
    } catch (err) {
      console.error("Error deleting order:", err);
      setSnackbar({
        open: true,
        message: err.message || "Failed to delete order",
        severity: "error",
      });
    }
  };

  const handleDeleteOrderCancel = () => {
    setDeleteOrderDialogOpen(false);
    setOrderToDelete(null);
  };

  if (loading) {
    return (
      <>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <Navigation />
          <Box
            component="main"
            sx={{
              backgroundColor: (theme) => theme.palette.background.default,
              flexGrow: 1,
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
            <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress />
              </Box>
            </Container>
          </Box>
        </Box>
      </>
    );
  }

  if (error && !organization) {
    return (
      <>
        <Box sx={{ display: "flex" }}>
          <CssBaseline />
          <Navigation />
          <Box
            component="main"
            sx={{
              backgroundColor: (theme) => theme.palette.background.default,
              flexGrow: 1,
              height: "100vh",
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
            }}
          >
            <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
            <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
              <Alert severity="error">{error}</Alert>
            </Container>
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <CssBaseline />
        <Navigation />
        <Box
          component="main"
          sx={{
            backgroundColor: (theme) => theme.palette.background.default,
            flexGrow: 1,
            height: "100vh",
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ minHeight: "48px !important", height: "48px" }} />
          <Container maxWidth="xl" sx={{ mt: 2, mb: 4, flexGrow: 1 }}>
            <Box sx={{ mb: 2 }}>
              <Breadcrumbs aria-label="breadcrumb">
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Admin
                </Typography>
                <Typography
                  color="primary"
                  onClick={() => navigate("/dashboard/organizations")}
                  sx={{
                    textDecoration: "none",
                    cursor: "pointer",
                    fontWeight: 500,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Organizations
                </Typography>
                <Typography color="text.primary" fontWeight={500}>
                  {organization?.organization_name || organization?.account_name || id}
                </Typography>
              </Breadcrumbs>
            </Box>

            <Box sx={{ mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="h4" component="h1" fontWeight="bold">
                {organization?.organization_name || organization?.account_name || "Organization"}
              </Typography>
              {organization && (
                <Button
                  variant="outlined"
                  startIcon={<CodeIcon />}
                  onClick={() => setOrganizationJsonModalOpen(true)}
                  size="small"
                >
                  View JSON
                </Button>
              )}
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Card>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label="Organization Details" />
                  <Tab label="User Access" />
                  <Tab label="Contacts" />
                  <Tab label="Orders" />
                  <Tab label="Subscriptions" />
                  <Tab label="Registered Agent" />
                </Tabs>
              </Box>

              <TabPanel value={tabValue} index={0}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                  {!editing && (
                    <Button
                      variant="contained"
                      startIcon={<EditIcon />}
                      onClick={() => setEditing(true)}
                    >
                      Edit
                    </Button>
                  )}
                  {editing && (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button variant="outlined" onClick={() => {
                        setEditing(false);
                        // Normalize zip code and EIN to digits-only for consistent formatting
                        const normalizedOrg = {
                          ...organization,
                          physical_zip: organization.physical_zip ? organization.physical_zip.replace(/\D/g, '').slice(0, 9) : organization.physical_zip,
                          organization_ein: organization.organization_ein ? organization.organization_ein.replace(/\D/g, '').slice(0, 9) : organization.organization_ein
                        };
                        setFormData(normalizedOrg);
                        setOrgNameError('');
                        setEmailError('');
                        setWebsiteError('');
                        setError(null);
                      }}>
                        Cancel
                      </Button>
                      <Button variant="contained" onClick={handleSave}>
                        Save
                      </Button>
                    </Box>
                  )}
                </Box>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Organization Name (organization_name) (required)"
                      value={formData.organization_name || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        const error = validateRequired(value, 'Organization name');
                        setOrgNameError(error);
                        setFormData({ ...formData, organization_name: value });
                      }}
                      onBlur={(e) => {
                        const error = validateRequired(e.target.value, 'Organization name');
                        setOrgNameError(error);
                      }}
                      error={!!orgNameError}
                      helperText={orgNameError}
                      required
                      disabled={!editing}
                      margin="dense"
                      size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="EIN (organization_ein)"
                      value={getFormattedEIN(formData.organization_ein)}
                      onChange={(e) => handleEINChange(e.target.value)}
                      disabled={!editing}
                      margin="dense"
                      size="small"
                      placeholder="12-3456789"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <StateSelect
                      label="Formation State (formation_state)"
                      value={formData.formation_state || ""}
                      onChange={(e) => setFormData({ ...formData, formation_state: e.target.value })}
                      disabled={!editing}
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Email (email)"
                      type="email"
                      value={formData.email || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        const error = validateEmail(value);
                        setEmailError(error);
                        setFormData({ ...formData, email: value });
                      }}
                      onBlur={(e) => {
                        const error = validateEmail(e.target.value);
                        setEmailError(error);
                      }}
                      error={!!emailError}
                      helperText={emailError}
                      disabled={!editing}
                      margin="dense"
                      size="small"
                      placeholder="name@example.com"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Phone (phone)"
                      value={getFormattedPhone(formData.phone || "")}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      disabled={!editing}
                      margin="dense"
                      size="small"
                      placeholder="(XXX) XXX-XXXX"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Office Phone (office_phone)"
                      value={getFormattedPhone(formData.office_phone || "")}
                      onChange={(e) => handleOfficePhoneChange(e.target.value)}
                      disabled={!editing}
                      margin="dense"
                      size="small"
                      placeholder="(XXX) XXX-XXXX"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Website (website)"
                      value={formData.website || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        const error = validateURL(value);
                        setWebsiteError(error);
                        setFormData({ ...formData, website: value });
                      }}
                      onBlur={(e) => {
                        const error = validateURL(e.target.value);
                        setWebsiteError(error);
                      }}
                      error={!!websiteError}
                      helperText={websiteError || "Enter a domain (e.g., example.com)"}
                      disabled={!editing}
                      margin="dense"
                      size="small"
                      placeholder="example.com"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Physical Address (physical_street)"
                      value={formData.physical_street || ""}
                      onChange={(e) => setFormData({ ...formData, physical_street: e.target.value })}
                      disabled={!editing}
                      margin="dense"
                size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="City (physical_city)"
                      value={formData.physical_city || ""}
                      onChange={(e) => setFormData({ ...formData, physical_city: e.target.value })}
                      disabled={!editing}
                      margin="dense"
                size="small"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <StateSelect
                      label="State (physical_state)"
                      value={formData.physical_state || formData.formation_state || ""}
                      onChange={(e) => setFormData({ ...formData, physical_state: e.target.value })}
                      disabled={!editing}
                      size="small"
                      margin="dense"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <TextField
                      fullWidth
                      label="ZIP (physical_zip)"
                      value={getFormattedZip(formData.physical_zip)}
                      onChange={(e) => handleZipChange(e.target.value)}
                      disabled={!editing}
                      margin="dense"
                      size="small"
                      placeholder="12345 or 12345-6789"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Nonprofit Type (nonprofit_type)"
                      value={formData.nonprofit_type || ""}
                      onChange={(e) => setFormData({ ...formData, nonprofit_type: e.target.value })}
                      disabled={!editing}
                      margin="dense"
                      size="small"
                      placeholder="e.g., Charitable, Religious, Educational, Scientific (501c3)"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Nonprofit Status (nonprofit_status)"
                      value={formData.nonprofit_status || ""}
                      onChange={(e) => setFormData({ ...formData, nonprofit_status: e.target.value })}
                      disabled={!editing}
                      margin="dense"
                      size="small"
                      placeholder="e.g., Active"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Activities (activities)"
                      value={formData.activities || ""}
                      onChange={(e) => setFormData({ ...formData, activities: e.target.value })}
                      disabled={!editing}
                      margin="dense"
                      size="small"
                      multiline
                      rows={4}
                      placeholder="Enter organization activities"
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Mission (organization_purpose)"
                      value={formData.organization_purpose || ""}
                      onChange={(e) => setFormData({ ...formData, organization_purpose: e.target.value })}
                      disabled={!editing}
                      margin="dense"
                      size="small"
                      multiline
                      rows={4}
                      placeholder="Enter organization mission/purpose"
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Box>
                    <Typography variant="h6" component="h2" fontWeight="bold" gutterBottom>
                      User Access
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage users who have access to this organization
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={refreshingUsers ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                      onClick={() => fetchUsers(true)}
                      disabled={refreshingUsers}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleOpenAddUserDialog}
                    >
                      Find and Attach
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={() => setCreateUserDialogOpen(true)}
                    >
                      Create User
                    </Button>
                  </Box>
                </Box>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadingUsers ? (
                        // Show skeleton rows when loading
                        Array.from({ length: 5 }).map((_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            <TableCell>
                              <Skeleton height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={32} width={120} />
                            </TableCell>
                            <TableCell>
                              <Skeleton height={24} width={60} />
                            </TableCell>
                            <TableCell align="right">
                              <Skeleton height={32} width={80} />
                            </TableCell>
                          </TableRow>
                        ))
                      ) : users.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary">
                              No users have access to this organization
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        users.map((userOrg) => (
                          <TableRow key={userOrg.id}>
                            <TableCell>{userOrg.name || "N/A"}</TableCell>
                            <TableCell>{userOrg.email || "N/A"}</TableCell>
                            <TableCell>
                              <Chip
                                label={formatOrganizationUserRoleLabel(userOrg.role)}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={userOrg.is_active ? "Active" : "Inactive"}
                                color={userOrg.is_active ? "success" : "error"}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                color="primary"
                                onClick={() => handleEditUserClick(userOrg)}
                                size="small"
                                sx={{ mr: 0.5 }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={() => handleRemoveUser(userOrg.user_id)}
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Box>
                    <Typography variant="h6" component="h2" fontWeight="bold" gutterBottom>
                      Contacts
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage the organization's contacts
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={refreshingContacts ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                      onClick={() => fetchContacts(true)}
                      disabled={refreshingContacts}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddContact}
                    >
                      Add Contact
                    </Button>
                  </Box>
                </Box>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Role</TableCell>
                        <TableCell>Board Member</TableCell>
                        <TableCell>Meta Data</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadingContacts ? (
                        // Show skeleton rows while loading
                        Array.from({ length: 5 }).map((_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            <TableCell>
                              <Skeleton width="70%" height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width="60%" height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width="50%" height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width="40%" height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width="30%" height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width="50%" height={20} />
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                                <Skeleton circle width={32} height={32} />
                                <Skeleton circle width={32} height={32} />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : contacts.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography color="text.secondary">
                              No contacts found for this organization
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        contacts.map((contact) => {
                          const isRegisteredAgent = contact.id === organization?.registered_agent;
                          return (
                            <TableRow 
                              key={contact.id}
                              sx={{
                                backgroundColor: isRegisteredAgent ? 'rgba(76, 175, 80, 0.1)' : 'inherit',
                                '&:hover': {
                                  backgroundColor: isRegisteredAgent 
                                    ? 'rgba(76, 175, 80, 0.15)' 
                                    : 'action.hover',
                                },
                              }}
                            >
                              <TableCell>
                                <Box>
                                  <Typography variant="body2">
                                    {contact.first_name || ""} {contact.last_name || ""}
                                  </Typography>
                                  {isRegisteredAgent && (
                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                      (Registered Agent)
                                    </Typography>
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell>{contact.email || "N/A"}</TableCell>
                              <TableCell>{contact.phone || contact.mobile || "N/A"}</TableCell>
                              <TableCell>{contact.role || "N/A"}</TableCell>
                              <TableCell>
                                {contact.is_board_member && (
                                  <CheckIcon color="success" fontSize="small" />
                                )}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                  {(() => {
                                    const raw = contact.meta_data;
                                    const meta = raw == null ? null : typeof raw === "string" ? (() => { try { const p = JSON.parse(raw); return p && typeof p === "object" ? p : null; } catch { return null; } })() : (typeof raw === "object" && !Array.isArray(raw) ? raw : null);
                                    return meta != null && Object.keys(meta).length > 0 ? (
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        fontFamily: "monospace",
                                        fontSize: "0.75rem",
                                        maxWidth: 140,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                      title={JSON.stringify(meta)}
                                    >
                                      {meta.squarespace_customer_id != null
                                        ? `SS ID: ${String(meta.squarespace_customer_id).slice(0, 12)}${String(meta.squarespace_customer_id).length > 12 ? "…" : ""}`
                                        : (() => {
                                            const s = JSON.stringify(meta);
                                            return s.length > 18 ? s.slice(0, 18) + "…" : s;
                                          })()}
                                    </Typography>
                                  ) : (
                                    <Typography variant="caption" color="text.secondary">
                                      —
                                    </Typography>
                                  );
                                  })()}
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                              <IconButton
                                color="primary"
                                onClick={() => handleEditContact(contact)}
                                size="small"
                                sx={{ mr: 0.5 }}
                                title="Edit Contact"
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                color="default"
                                onClick={() => {
                                  setSelectedContactForJson(contact);
                                  setContactJsonModalOpen(true);
                                }}
                                size="small"
                                sx={{ mr: 0.5 }}
                                title="View JSON"
                              >
                                <CodeIcon />
                              </IconButton>
                              {isRegisteredAgent ? (
                                <IconButton
                                  color="disabled"
                                  size="small"
                                  title="Cannot delete registered agent"
                                  disabled
                                >
                                  <BlockIcon />
                                </IconButton>
                              ) : (
                                <IconButton
                                  color="error"
                                  onClick={() => handleDeleteContactClick(contact)}
                                  size="small"
                                  title="Delete Contact"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Squarespace websites are attached to a contact.</strong> The Squarespace customer ID is stored in{" "}
                    <Box component="code" sx={{ fontFamily: "monospace", fontSize: "0.9em", bgcolor: "action.hover", px: 0.5, py: 0.25, borderRadius: 0.5 }}>
                      contact.meta_data.squarespace_customer_id
                    </Box>
                    {" "}— this links the contact to their Squarespace account when a website order is created.
                  </Typography>
                </Alert>
              </TabPanel>

              <TabPanel value={tabValue} index={3}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Box>
                    <Typography variant="h6" component="h2" fontWeight="bold" gutterBottom>
                      Orders
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage the organization's orders
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={refreshingOrders ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                      onClick={() => fetchOrders(true)}
                      disabled={refreshingOrders}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddOrderClick}
                    >
                      Add Order
                    </Button>
                  </Box>
                </Box>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Order Number</TableCell>
                        <TableCell>Order Date</TableCell>
                        <TableCell>Product Name</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadingOrders ? (
                        // Show skeleton rows while loading
                        Array.from({ length: 5 }).map((_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            <TableCell>
                              <Skeleton width="60%" height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width="50%" height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width="70%" height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width="40%" height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width={80} height={24} borderRadius={4} />
                            </TableCell>
                            <TableCell>
                              <Box sx={{ display: "flex", gap: 0.5 }}>
                                <Skeleton circle width={32} height={32} />
                                <Skeleton circle width={32} height={32} />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : orders.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            <Typography color="text.secondary">
                              No orders found for this organization
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        orders.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell>{order.order_number || "N/A"}</TableCell>
                          <TableCell>
                            {order.order_date
                              ? new Date(order.order_date).toLocaleDateString()
                              : "N/A"}
                          </TableCell>
                          <TableCell>{order.product_name || "N/A"}</TableCell>
                          <TableCell>
                            {order.amount
                              ? `$${parseFloat(order.amount).toFixed(2)}`
                              : "N/A"}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={order.status || "Unknown"}
                              color={
                                order.status === "Completed"
                                  ? "success"
                                  : order.status === "Pending"
                                  ? "warning"
                                  : order.status === "Cancelled"
                                  ? "error"
                                  : "default"
                              }
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton
                              color="primary"
                              onClick={() => handleEditOrderClick(order)}
                              size="small"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => handleDeleteOrderClick(order)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>

              <TabPanel value={tabValue} index={4}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Box>
                    <Typography variant="h6" component="h2" fontWeight="bold" gutterBottom>
                      Subscriptions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Manage the organization's subscriptions
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="outlined"
                      startIcon={refreshingSubscriptions ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                      onClick={() => fetchSubscriptions(true)}
                      disabled={refreshingSubscriptions}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddSubscriptionClick}
                    >
                      Add Subscription
                    </Button>
                  </Box>
                </Box>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Term</TableCell>
                        <TableCell>Start / End</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadingSubscriptions ? (
                        // Show skeleton rows while loading
                        Array.from({ length: 5 }).map((_, index) => (
                          <TableRow key={`skeleton-${index}`}>
                            <TableCell>
                              <Skeleton width="60%" height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width="50%" height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width="40%" height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width="60%" height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width="40%" height={20} />
                            </TableCell>
                            <TableCell>
                              <Skeleton width="50%" height={20} />
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
                                <Skeleton circle width={32} height={32} />
                                <Skeleton circle width={32} height={32} />
                                <Skeleton circle width={32} height={32} />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : subscriptions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography color="text.secondary">
                              No subscriptions found for this organization
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        subscriptions.map((subscription) => (
                          <TableRow key={subscription.id}>
                            <TableCell>{subscription.plan_details?.name || subscription.name || subscription.subscription_type || "N/A"}</TableCell>
                            <TableCell>{subscription.plan_details?.vendor || "N/A"}</TableCell>
                            <TableCell>{subscription.plan_details?.term ? `${subscription.plan_details.term.charAt(0).toUpperCase()}${subscription.plan_details.term.slice(1).toLowerCase()}` : "N/A"}</TableCell>
                            <TableCell>
                              {subscription.start_date || subscription.end_date
                                ? `${subscription.start_date ? new Date(subscription.start_date).toLocaleDateString() : "—"} / ${subscription.end_date ? new Date(subscription.end_date).toLocaleDateString() : "—"}`
                                : "N/A"}
                            </TableCell>
                            <TableCell>
                              {subscription.amount
                                ? `${subscription.currency || "$"}${parseFloat(subscription.amount).toFixed(2)}`
                                : "N/A"}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={
                                  subscription.status
                                    ? subscription.status.charAt(0).toUpperCase() +
                                      subscription.status.slice(1).toLowerCase()
                                    : "—"
                                }
                                size="small"
                                color={
                                  !subscription.status
                                    ? "default"
                                    : subscription.status.toLowerCase() === "active"
                                      ? "success"
                                      : ["cancelled", "canceled", "expired"].includes(subscription.status.toLowerCase())
                                        ? "error"
                                        : subscription.status.toLowerCase() === "paused"
                                          ? "warning"
                                          : "default"
                                }
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                onClick={() => navigate(`/dashboard/subscriptions/${subscription.id}`)}
                                sx={{ mr: 0.5 }}
                                title="View Subscription"
                              >
                                View
                              </Button>
                              <IconButton
                                color="default"
                                onClick={() => {
                                  setSelectedSubscriptionForJson(subscription);
                                  setSubscriptionJsonModalOpen(true);
                                }}
                                size="small"
                                sx={{ mr: 0.5 }}
                                title="View JSON"
                              >
                                <CodeIcon />
                              </IconButton>
                              <IconButton
                                color="error"
                                onClick={() => handleDeleteSubscriptionClick(subscription)}
                                size="small"
                                title="Delete Subscription"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </TabPanel>

              <TabPanel value={tabValue} index={5}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                  <Box>
                    <Typography variant="h6" component="h2" fontWeight="bold" gutterBottom>
                      Registered Agent
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {editingRegisteredAgent ? "Edit registered agent information" : "View registered agent information for this organization"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    {organization && !editingRegisteredAgent && (
                      <IconButton
                        color="default"
                        onClick={() => {
                          const registeredAgentData = {
                            registered_agent_name: organization?.registered_agent_name || (organization?.registered_agent && contacts.find(c => c.id === organization.registered_agent) ? `${contacts.find(c => c.id === organization.registered_agent).first_name || ''} ${contacts.find(c => c.id === organization.registered_agent).last_name || ''}`.trim() : null),
                            registered_agent_type: organization?.registered_agent_type,
                            registered_agent_services: organization?.registered_agent_services,
                            registered_agent_email: organization?.registered_agent_email || (organization?.registered_agent && contacts.find(c => c.id === organization.registered_agent)?.email),
                            registered_agent_phone: organization?.registered_agent_phone || (organization?.registered_agent && contacts.find(c => c.id === organization.registered_agent)?.phone),
                            registered_agent_street: organization?.registered_agent_street || (organization?.registered_agent && contacts.find(c => c.id === organization.registered_agent)?.street),
                            registered_agent_city: organization?.registered_agent_city || (organization?.registered_agent && contacts.find(c => c.id === organization.registered_agent)?.city),
                            registered_agent_state: organization?.registered_agent_state || (organization?.registered_agent && contacts.find(c => c.id === organization.registered_agent)?.state),
                            registered_agent_zip: organization?.registered_agent_zip || (organization?.registered_agent && contacts.find(c => c.id === organization.registered_agent)?.zip),
                            registered_agent_use_different_address: organization?.registered_agent_use_different_address,
                            registered_agent: organization?.registered_agent,
                          };
                          setSelectedRegisteredAgentForJson(registeredAgentData);
                          setRegisteredAgentJsonModalOpen(true);
                        }}
                        size="small"
                        title="View JSON"
                      >
                        <CodeIcon />
                      </IconButton>
                    )}
                    {!editingRegisteredAgent && (
                      <>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            // Initialize date fields with current organization values
                            setRegisteredAgentRenewalDate(
                              organization?.registered_agent_renewal_date 
                                ? new Date(organization.registered_agent_renewal_date).toISOString().split("T")[0] 
                                : ""
                            );
                            setRegisteredAgentRenewalDateMonth(organization?.registered_agent_renewal_date_month || "");
                            setRegisteredAgentRenewalDateDay(organization?.registered_agent_renewal_date_day || "");
                            setSelectContactDialogOpen(true);
                          }}
                          sx={{ mr: 1 }}
                        >
                          Select Contact
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={<EditIcon />}
                          onClick={() => {
                            const currentData = {
                              registered_agent_name: organization?.registered_agent_name || "",
                              registered_agent_type: organization?.registered_agent_type || "",
                              registered_agent_street: organization?.registered_agent_street || "",
                              registered_agent_city: organization?.registered_agent_city || "",
                              registered_agent_state: organization?.registered_agent_state || "",
                              registered_agent_zip: organization?.registered_agent_zip || "",
                              registered_agent_use_different_address: organization?.registered_agent_use_different_address || false,
                              registered_agent_renewal_date: organization?.registered_agent_renewal_date ? new Date(organization.registered_agent_renewal_date).toISOString().split("T")[0] : "",
                              registered_agent_renewal_date_month: organization?.registered_agent_renewal_date_month || "",
                              registered_agent_renewal_date_day: organization?.registered_agent_renewal_date_day || "",
                              registered_agent: organization?.registered_agent || null,
                            };
                            setRegisteredAgentFormData(currentData);
                            setEditingRegisteredAgent(true);
                          }}
                        >
                          Edit
                        </Button>
                      </>
                    )}
                    {editingRegisteredAgent && (
                      <Button
                        variant="outlined"
                        onClick={() => {
                          // Initialize date fields with current form data or organization values
                          setRegisteredAgentRenewalDate(
                            registeredAgentFormData.registered_agent_renewal_date || 
                            (organization?.registered_agent_renewal_date 
                              ? new Date(organization.registered_agent_renewal_date).toISOString().split("T")[0] 
                              : "")
                          );
                          setRegisteredAgentRenewalDateMonth(
                            registeredAgentFormData.registered_agent_renewal_date_month || 
                            organization?.registered_agent_renewal_date_month || 
                            ""
                          );
                          setRegisteredAgentRenewalDateDay(
                            registeredAgentFormData.registered_agent_renewal_date_day || 
                            organization?.registered_agent_renewal_date_day || 
                            ""
                          );
                          setSelectContactDialogOpen(true);
                        }}
                        sx={{ mr: 1 }}
                      >
                        Select Contact
                      </Button>
                    )}
                    {editingRegisteredAgent && (
                      <Box sx={{ display: "flex", gap: 1 }}>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setEditingRegisteredAgent(false);
                            setRegisteredAgentFormData({});
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSaveRegisteredAgent}
                        >
                          Save
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
                <Grid container spacing={2}>
                  {/* Show contact info panel if a contact is linked */}
                  {((editingRegisteredAgent && registeredAgentFormData.registered_agent) || (!editingRegisteredAgent && organization?.registered_agent)) && (
                    <Grid size={{ xs: 12 }}>
                      <Box sx={{ p: 2, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Linked Contact:
                          </Typography>
                          {editingRegisteredAgent ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={() => {
                                setRegisteredAgentFormData({
                                  ...registeredAgentFormData,
                                  registered_agent: null,
                                });
                              }}
                            >
                              Unlink Contact
                            </Button>
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              color="error"
                              onClick={handleClearRegisteredAgentContact}
                            >
                              Clear Link
                            </Button>
                          )}
                        </Box>
                        {(() => {
                          const contactId = editingRegisteredAgent ? registeredAgentFormData.registered_agent : organization?.registered_agent;
                          const contact = contacts.find(c => c.id === contactId);
                          if (!contact) return <Typography variant="body2" color="text.secondary">Contact not found</Typography>;
                          
                          return (
                            <Box>
                              <Typography variant="body2" sx={{ mb: 0.5 }}>
                                <strong>Name:</strong> {`${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Unnamed Contact'}
                              </Typography>
                              {contact.email && (
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Email:</strong> {contact.email}
                                </Typography>
                              )}
                              {contact.phone && (
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Phone:</strong> {contact.phone}
                                </Typography>
                              )}
                              {(contact.street || contact.city || contact.state || contact.zip) && (
                                <Typography variant="body2">
                                  <strong>Address:</strong> {[
                                    contact.street,
                                    contact.city,
                                    contact.state,
                                    contact.zip
                                  ].filter(Boolean).join(', ') || 'N/A'}
                                </Typography>
                              )}
                            </Box>
                          );
                        })()}
                      </Box>
                    </Grid>
                  )}
                  
                  {/* Show manual fields only if no contact is linked */}
                  {!((editingRegisteredAgent && registeredAgentFormData.registered_agent) || (!editingRegisteredAgent && organization?.registered_agent)) && (
                    <>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label={editingRegisteredAgent ? "Registered Agent Name (registered_agent_name)" : "Registered Agent Name"}
                          value={editingRegisteredAgent ? (registeredAgentFormData.registered_agent_name || "") : (organization?.registered_agent_name || "N/A")}
                          onChange={(e) => setRegisteredAgentFormData({ ...registeredAgentFormData, registered_agent_name: e.target.value })}
                          disabled={!editingRegisteredAgent}
                          margin="dense"
                          size="small"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        {editingRegisteredAgent ? (
                          <FormControl fullWidth margin="dense" size="small">
                            <InputLabel id="registered-agent-type-label">Registered Agent Type (registered_agent_type)</InputLabel>
                            <Select
                              labelId="registered-agent-type-label"
                              label="Registered Agent Type (registered_agent_type)"
                              value={registeredAgentFormData.registered_agent_type || ""}
                              onChange={(e) => setRegisteredAgentFormData({ ...registeredAgentFormData, registered_agent_type: e.target.value || null })}
                            >
                              <MenuItem value="">Null</MenuItem>
                              <MenuItem value="Self">Self</MenuItem>
                              <MenuItem value="Individual">Individual</MenuItem>
                              <MenuItem value="Third Party">Third Party</MenuItem>
                            </Select>
                          </FormControl>
                        ) : (
                          <TextField
                            fullWidth
                            label="Registered Agent Type"
                            value={organization?.registered_agent_type || "N/A"}
                            disabled
                            margin="dense"
                            size="small"
                          />
                        )}
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label={editingRegisteredAgent ? "Registered Agent Street Address (registered_agent_street)" : "Registered Agent Street Address"}
                          value={editingRegisteredAgent ? (registeredAgentFormData.registered_agent_street || "") : (organization?.registered_agent_street || "N/A")}
                          onChange={(e) => setRegisteredAgentFormData({ ...registeredAgentFormData, registered_agent_street: e.target.value })}
                          disabled={!editingRegisteredAgent}
                          margin="dense"
                          size="small"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <TextField
                          fullWidth
                          label={editingRegisteredAgent ? "Registered Agent City (registered_agent_city)" : "Registered Agent City"}
                          value={editingRegisteredAgent ? (registeredAgentFormData.registered_agent_city || "") : (organization?.registered_agent_city || "N/A")}
                          onChange={(e) => setRegisteredAgentFormData({ ...registeredAgentFormData, registered_agent_city: e.target.value })}
                          disabled={!editingRegisteredAgent}
                          margin="dense"
                          size="small"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <StateSelect
                          label={editingRegisteredAgent ? "Registered Agent State (registered_agent_state)" : "Registered Agent State"}
                          value={editingRegisteredAgent ? (registeredAgentFormData.registered_agent_state || "") : (organization?.registered_agent_state || "")}
                          onChange={(e) => setRegisteredAgentFormData({ ...registeredAgentFormData, registered_agent_state: e.target.value })}
                          disabled={!editingRegisteredAgent}
                          size="small"
                          margin="dense"
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                          fullWidth
                          label={editingRegisteredAgent ? "Registered Agent ZIP (registered_agent_zip)" : "Registered Agent ZIP"}
                          value={editingRegisteredAgent ? (registeredAgentFormData.registered_agent_zip || "") : (organization?.registered_agent_zip || "N/A")}
                          onChange={(e) => setRegisteredAgentFormData({ ...registeredAgentFormData, registered_agent_zip: e.target.value })}
                          disabled={!editingRegisteredAgent}
                          margin="dense"
                          size="small"
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={editingRegisteredAgent ? (registeredAgentFormData.registered_agent_use_different_address || false) : (organization?.registered_agent_use_different_address || false)}
                              onChange={(e) => setRegisteredAgentFormData({ ...registeredAgentFormData, registered_agent_use_different_address: e.target.checked })}
                              disabled={!editingRegisteredAgent}
                            />
                          }
                          label={editingRegisteredAgent ? "Use Different Address (registered_agent_use_different_address)" : "Use Different Address"}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label={editingRegisteredAgent ? "Registered Agent Renewal Date (registered_agent_renewal_date)" : "Registered Agent Renewal Date"}
                      type="date"
                      value={editingRegisteredAgent ? (registeredAgentFormData.registered_agent_renewal_date || "") : (organization?.registered_agent_renewal_date ? new Date(organization.registered_agent_renewal_date).toISOString().split("T")[0] : "")}
                      onChange={(e) => setRegisteredAgentFormData({ ...registeredAgentFormData, registered_agent_renewal_date: e.target.value })}
                      disabled={!editingRegisteredAgent}
                      margin="dense"
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label={editingRegisteredAgent ? "Renewal Date Month (registered_agent_renewal_date_month)" : "Renewal Date Month"}
                      value={editingRegisteredAgent ? (registeredAgentFormData.registered_agent_renewal_date_month || "") : (organization?.registered_agent_renewal_date_month || "")}
                      onChange={(e) => setRegisteredAgentFormData({ ...registeredAgentFormData, registered_agent_renewal_date_month: e.target.value })}
                      disabled={!editingRegisteredAgent}
                      margin="dense"
                      size="small"
                      placeholder="e.g., January"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      fullWidth
                      label={editingRegisteredAgent ? "Renewal Date Day (registered_agent_renewal_date_day)" : "Renewal Date Day"}
                      value={editingRegisteredAgent ? (registeredAgentFormData.registered_agent_renewal_date_day || "") : (organization?.registered_agent_renewal_date_day || "")}
                      onChange={(e) => setRegisteredAgentFormData({ ...registeredAgentFormData, registered_agent_renewal_date_day: e.target.value })}
                      disabled={!editingRegisteredAgent}
                      margin="dense"
                      size="small"
                      placeholder="e.g., 15"
                    />
                  </Grid>
                </Grid>
              </TabPanel>

            </Card>
          </Container>
        </Box>
      </Box>

      <AddUserToOrgDialog
        open={addUserDialogOpen}
        onClose={() => setAddUserDialogOpen(false)}
        onReset={() => {
          setSelectedUserId("");
          setSelectedUserRole("viewer");
          setUserSearchQuery("");
        }}
        searchQuery={userSearchQuery}
        onSearchChange={handleSearchChange}
        availableUsers={availableUsers}
        loadingAvailableUsers={loadingAvailableUsers}
        pagination={userPagination}
        onPageChange={handleUserPageChange}
        existingUserIds={users.map((u) => u.user_id)}
        onAddUser={handleAddUser}
      />

      <CreateUserDialog
        open={createUserDialogOpen}
        onClose={() => {
          setCreateUserDialogOpen(false);
          setNewUserFormData({ name: "", email: "" });
        }}
        formData={newUserFormData}
        onFormDataChange={setNewUserFormData}
        onCreate={handleCreateUser}
      />

      <EditUserDialog
        open={editUserDialogOpen}
        onClose={handleCloseUserDialog}
        formData={userFormData}
        onFormDataChange={setUserFormData}
        onSave={handleSaveUser}
        permissionsError={permissionsError}
        onPermissionsErrorChange={setPermissionsError}
      />

      <ContactDialog
        open={editContactDialogOpen || addContactDialogOpen}
        onClose={handleCloseContactDialog}
        editing={!!editingContact}
        formData={contactFormData}
        onFormDataChange={setContactFormData}
        onSave={handleSaveContact}
      />

      <JsonDataModal
        open={contactJsonModalOpen}
        onClose={() => {
          setContactJsonModalOpen(false);
          setSelectedContactForJson(null);
        }}
        data={selectedContactForJson}
        title={`Contact JSON - ${selectedContactForJson ? `${selectedContactForJson.first_name || ''} ${selectedContactForJson.last_name || ''}`.trim() || selectedContactForJson.email || 'Unknown' : ''}`}
      />

      <JsonDataModal
        open={subscriptionJsonModalOpen}
        onClose={() => {
          setSubscriptionJsonModalOpen(false);
          setSelectedSubscriptionForJson(null);
        }}
        data={selectedSubscriptionForJson}
        title={`Subscription JSON - ${selectedSubscriptionForJson ? (selectedSubscriptionForJson.name || selectedSubscriptionForJson.subscription_type || 'Unknown') : ''}`}
      />

      <JsonDataModal
        open={registeredAgentJsonModalOpen}
        onClose={() => {
          setRegisteredAgentJsonModalOpen(false);
          setSelectedRegisteredAgentForJson(null);
        }}
        data={selectedRegisteredAgentForJson}
        title={`Registered Agent JSON - ${selectedRegisteredAgentForJson ? (selectedRegisteredAgentForJson.registered_agent_name || 'Unknown') : ''}`}
      />

      <JsonDataModal
        open={organizationJsonModalOpen}
        onClose={() => {
          setOrganizationJsonModalOpen(false);
        }}
        data={organization}
        title={`Organization JSON - ${organization ? (organization.organization_name || organization.account_name || 'Unknown') : ''}`}
      />

      <DeleteContactDialog
        open={deleteContactDialogOpen}
        onClose={handleDeleteContactCancel}
        onConfirm={handleDeleteContactConfirm}
        contact={contactToDelete}
      />

      <SelectContactForRegisteredAgentDialog
        open={selectContactDialogOpen}
        onClose={() => {
          setSelectContactDialogOpen(false);
          setSelectedContactForRegisteredAgent(null);
          setRegisteredAgentRenewalDate("");
          setRegisteredAgentRenewalDateMonth("");
          setRegisteredAgentRenewalDateDay("");
        }}
        organization={organization}
        contacts={contacts}
        selectedContact={selectedContactForRegisteredAgent}
        onSelectedContactChange={setSelectedContactForRegisteredAgent}
        renewalDate={registeredAgentRenewalDate}
        renewalDateMonth={registeredAgentRenewalDateMonth}
        renewalDateDay={registeredAgentRenewalDateDay}
        onRenewalDateChange={(v) => setRegisteredAgentRenewalDate(v)}
        onRenewalDateMonthChange={(v) => setRegisteredAgentRenewalDateMonth(v)}
        onRenewalDateDayChange={(v) => setRegisteredAgentRenewalDateDay(v)}
        onConfirm={handleSelectContactForRegisteredAgent}
      />

      <OrderDialog
        open={editOrderDialogOpen || addOrderDialogOpen}
        onClose={handleCloseOrderDialog}
        editing={!!editingOrder}
        formData={orderFormData}
        onFormDataChange={setOrderFormData}
        onSave={handleSaveOrder}
      />

      <DeleteOrderDialog
        open={deleteOrderDialogOpen}
        onClose={handleDeleteOrderCancel}
        onConfirm={handleDeleteOrderConfirm}
        order={orderToDelete}
      />

      <DeleteSubscriptionDialog
        open={deleteSubscriptionDialogOpen}
        onClose={handleDeleteSubscriptionCancel}
        onConfirm={handleDeleteSubscriptionConfirm}
        subscription={subscriptionToDelete}
      />

      <SubscriptionDialog
        open={addSubscriptionDialogOpen || editSubscriptionDialogOpen}
        onClose={handleCloseSubscriptionDialog}
        editing={!!editingSubscription}
        formData={subscriptionFormData}
        onFormDataChange={setSubscriptionFormData}
        onSave={handleSaveSubscription}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
